import { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import {
  Lock, LogOut, Mail, Phone, Search, Trash2, RefreshCw, Inbox, CheckCircle2, Archive, ArrowLeft, Images, Settings as SettingsIcon, MessagesSquare,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import AdminPortfolio from "@/components/admin/AdminPortfolio";
import AdminSettings from "@/components/admin/AdminSettings";
import AdminChats from "@/components/admin/AdminChats";
import LeadDetailDialog from "@/components/admin/LeadDetailDialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "fp_admin_token";

export default function AdminPage() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  if (!token) return <LoginScreen onLogin={(t) => { localStorage.setItem(TOKEN_KEY, t); setToken(t); }} />;
  return <Dashboard token={token} onLogout={() => { localStorage.removeItem(TOKEN_KEY); setToken(""); }} />;
}

function LoginScreen({ onLogin }) {
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/admin/login`, { password });
      toast.success("Welcome back.");
      onLogin(data.token);
    } catch (err) {
      toast.error("Invalid password.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="admin-login" className="min-h-screen bg-bone grid place-items-center px-6">
      <div className="w-full max-w-md bg-white border border-border p-10 rounded-md shadow-sm">
        <Link to="/" className="text-xs uppercase tracking-[0.25em] text-muted-foreground hover:text-sunrise inline-flex items-center gap-2">
          <ArrowLeft className="w-3.5 h-3.5" /> Back to site
        </Link>
        <div className="mt-6 flex items-center gap-3">
          <span className="w-10 h-10 grid place-items-center bg-navy text-white rounded-md">
            <Lock className="w-4 h-4" />
          </span>
          <div>
            <div className="font-display font-extrabold text-xl tracking-tight">Admin Console</div>
            <div className="text-xs text-muted-foreground">Five Peaks Construction</div>
          </div>
        </div>

        <form onSubmit={submit} className="mt-8 space-y-4">
          <label className="block">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-navy">Password</span>
            <Input
              data-testid="admin-password-input"
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="mt-2 rounded-md"
              placeholder="Enter admin password"
            />
          </label>
          <Button
            type="submit"
            data-testid="admin-login-submit"
            disabled={loading}
            className="w-full bg-navy hover:bg-navy/90 text-white rounded-md"
          >
            {loading ? "Signing in…" : "Sign in"}
          </Button>
        </form>
      </div>
    </div>
  );
}

function Dashboard({ token, onLogout }) {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState("all");
  const [editingLead, setEditingLead] = useState(null);

  const fetchLeads = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/leads`, { headers: { "X-Admin-Token": token } });
      setLeads(data);
    } catch (err) {
      if (err?.response?.status === 401) {
        toast.error("Session expired. Please sign in again.");
        onLogout();
      } else {
        toast.error("Failed to load leads.");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchLeads(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const updateStatus = async (id, status) => {
    try {
      const { data } = await axios.patch(`${API}/admin/leads/${id}`, { status }, { headers: { "X-Admin-Token": token } });
      setLeads((ls) => ls.map((l) => (l.id === id ? data : l)));
      toast.success(`Marked as ${status}.`);
    } catch {
      toast.error("Update failed.");
    }
  };

  const handleLeadSaved = (saved) => {
    setLeads((ls) => ls.map((l) => (l.id === saved.id ? saved : l)));
  };

  const deleteLead = async (id) => {
    if (!window.confirm("Delete this lead? This cannot be undone.")) return;
    try {
      await axios.delete(`${API}/admin/leads/${id}`, { headers: { "X-Admin-Token": token } });
      setLeads((ls) => ls.filter((l) => l.id !== id));
      toast.success("Lead deleted.");
    } catch {
      toast.error("Delete failed.");
    }
  };

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return leads.filter((l) => {
      if (filter !== "all" && l.status !== filter) return false;
      if (!q) return true;
      return [l.name, l.email, l.phone, l.service_type, l.message].some((v) => (v || "").toLowerCase().includes(q));
    });
  }, [leads, query, filter]);

  const stats = useMemo(() => ({
    total: leads.length,
    new: leads.filter((l) => l.status === "new").length,
    contacted: leads.filter((l) => l.status === "contacted").length,
    archived: leads.filter((l) => l.status === "archived").length,
  }), [leads]);

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-bone">
      {/* Header */}
      <div className="bg-navy text-white">
        <div className="px-6 md:px-10 py-6 flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <span className="w-10 h-10 grid place-items-center bg-sunrise rounded-md">
              <Inbox className="w-4 h-4" />
            </span>
            <div>
              <div className="font-display font-extrabold text-lg tracking-tight">Admin Console</div>
              <div className="text-xs text-white/60">Five Peaks Construction · Quote Leads</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/" data-testid="admin-back-site">
              <Button variant="outline" className="rounded-md bg-transparent text-white border-white/30 hover:bg-white hover:text-navy">
                <ArrowLeft className="w-4 h-4 mr-2" /> Site
              </Button>
            </Link>
            <Button onClick={fetchLeads} variant="outline" data-testid="admin-refresh" className="rounded-md bg-transparent text-white border-white/30 hover:bg-white hover:text-navy">
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} /> Refresh
            </Button>
            <Button onClick={onLogout} data-testid="admin-logout" className="rounded-md bg-sunrise hover:bg-sunrise-hover">
              <LogOut className="w-4 h-4 mr-2" /> Logout
            </Button>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="px-6 md:px-10 -mt-px pb-12">
        <Tabs defaultValue="leads" className="w-full">
          <TabsList className="bg-white border border-border rounded-md p-1 -translate-y-6" data-testid="admin-tabs">
            <TabsTrigger value="leads" data-testid="tab-leads" className="rounded-md data-[state=active]:bg-navy data-[state=active]:text-white">
              <Inbox className="w-4 h-4 mr-2" /> Leads
            </TabsTrigger>
            <TabsTrigger value="portfolio" data-testid="tab-portfolio" className="rounded-md data-[state=active]:bg-navy data-[state=active]:text-white">
              <Images className="w-4 h-4 mr-2" /> Portfolio
            </TabsTrigger>
            <TabsTrigger value="chats" data-testid="tab-chats" className="rounded-md data-[state=active]:bg-navy data-[state=active]:text-white">
              <MessagesSquare className="w-4 h-4 mr-2" /> Chats
            </TabsTrigger>
            <TabsTrigger value="settings" data-testid="tab-settings" className="rounded-md data-[state=active]:bg-navy data-[state=active]:text-white">
              <SettingsIcon className="w-4 h-4 mr-2" /> Settings
            </TabsTrigger>
          </TabsList>

          <TabsContent value="leads" className="mt-0 space-y-5">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <StatCard label="Total Leads" value={stats.total} testid="stat-total" />
              <StatCard label="New" value={stats.new} accent="bg-sunrise" testid="stat-new" />
              <StatCard label="Contacted" value={stats.contacted} testid="stat-contacted" />
              <StatCard label="Archived" value={stats.archived} testid="stat-archived" />
            </div>

            <div className="bg-white border border-border rounded-md p-4 flex flex-wrap items-center gap-3">
              <div className="relative flex-1 min-w-[200px]">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  data-testid="admin-search"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search by name, email, service…"
                  className="pl-9 rounded-md"
                />
              </div>
              <Select value={filter} onValueChange={setFilter}>
                <SelectTrigger className="w-[180px] rounded-md" data-testid="admin-filter-status">
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All statuses</SelectItem>
                  <SelectItem value="new">New</SelectItem>
                  <SelectItem value="contacted">Contacted</SelectItem>
                  <SelectItem value="quoted">Quoted</SelectItem>
                  <SelectItem value="won">Won</SelectItem>
                  <SelectItem value="lost">Lost</SelectItem>
                  <SelectItem value="archived">Archived</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="bg-white border border-border rounded-md overflow-hidden">
              <Table data-testid="admin-leads-table">
            <TableHeader>
              <TableRow className="bg-secondary/50">
                <TableHead>Lead</TableHead>
                <TableHead className="hidden md:table-cell">Service</TableHead>
                <TableHead className="hidden lg:table-cell">Message</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="hidden md:table-cell">Submitted</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={6} className="text-center py-12 text-muted-foreground">Loading leads…</TableCell></TableRow>
              ) : filtered.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-16 text-muted-foreground">
                    <Inbox className="w-8 h-8 mx-auto mb-3 opacity-60" />
                    No leads match your filters yet.
                  </TableCell>
                </TableRow>
              ) : (
                filtered.map((l) => (
                  <TableRow key={l.id} data-testid={`admin-lead-row-${l.id}`} className="hover:bg-secondary/30">
                    <TableCell className="align-top py-4">
                      <div className="font-semibold text-navy">{l.name}</div>
                      <div className="text-xs text-muted-foreground flex items-center gap-1.5 mt-1">
                        <Mail className="w-3 h-3" />
                        <a href={`mailto:${l.email}`} className="hover:text-sunrise break-all">{l.email}</a>
                      </div>
                      <div className="text-xs text-muted-foreground flex items-center gap-1.5 mt-0.5">
                        <Phone className="w-3 h-3" />
                        <a href={`tel:${l.phone}`} className="hover:text-sunrise">{l.phone}</a>
                      </div>
                    </TableCell>
                    <TableCell className="hidden md:table-cell align-top py-4">
                      <div className="text-sm font-medium">{l.service_type}</div>
                      <div className="text-xs text-muted-foreground capitalize mt-1">{l.project_type}</div>
                    </TableCell>
                    <TableCell className="hidden lg:table-cell align-top py-4 max-w-md">
                      <p className="text-sm text-muted-foreground line-clamp-3">{l.message}</p>
                    </TableCell>
                    <TableCell className="align-top py-4">
                      <StatusBadge status={l.status} />
                      {l.email_sent && (
                        <div className="text-[10px] uppercase tracking-wider text-emerald-600 mt-1 flex items-center gap-1">
                          <CheckCircle2 className="w-3 h-3" /> emailed
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="hidden md:table-cell align-top py-4 text-xs text-muted-foreground">
                      {formatDate(l.created_at)}
                    </TableCell>
                    <TableCell className="align-top py-4">
                      <div className="flex items-center justify-end gap-2 flex-wrap">
                        <Button
                          size="sm"
                          onClick={() => setEditingLead(l)}
                          data-testid={`edit-lead-${l.id}`}
                          className="rounded-md text-xs h-8 bg-navy hover:bg-navy/90"
                        >
                          Open
                        </Button>
                        {l.status !== "contacted" && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => updateStatus(l.id, "contacted")}
                            data-testid={`mark-contacted-${l.id}`}
                            className="rounded-md text-xs h-8"
                          >
                            <CheckCircle2 className="w-3 h-3 mr-1" /> Contacted
                          </Button>
                        )}
                        {l.status !== "archived" && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => updateStatus(l.id, "archived")}
                            data-testid={`archive-${l.id}`}
                            className="rounded-md text-xs h-8"
                          >
                            <Archive className="w-3 h-3 mr-1" /> Archive
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => deleteLead(l.id)}
                          data-testid={`delete-${l.id}`}
                          className="rounded-md text-xs h-8 text-destructive hover:bg-destructive hover:text-white"
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
            </div>
          </TabsContent>

          <TabsContent value="portfolio" className="mt-0">
            <AdminPortfolio token={token} />
          </TabsContent>

          <TabsContent value="chats" className="mt-0">
            <AdminChats token={token} />
          </TabsContent>

          <TabsContent value="settings" className="mt-0">
            <AdminSettings token={token} />
          </TabsContent>
        </Tabs>
      </div>

      <LeadDetailDialog
        open={!!editingLead}
        onOpenChange={(o) => { if (!o) setEditingLead(null); }}
        lead={editingLead}
        token={token}
        onSaved={handleLeadSaved}
      />
    </div>
  );
}

function StatCard({ label, value, accent = "bg-navy", testid }) {
  return (
    <div data-testid={testid} className="bg-white border border-border rounded-md p-5 shadow-sm">
      <div className="flex items-start justify-between">
        <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground font-semibold">{label}</div>
        <span className={`w-2 h-2 rounded-full ${accent}`} />
      </div>
      <div className="mt-3 num-label text-3xl text-navy">{value}</div>
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    new: "bg-sunrise text-white",
    contacted: "bg-blue-600 text-white",
    quoted: "bg-indigo-600 text-white",
    won: "bg-emerald-600 text-white",
    lost: "bg-gray-500 text-white",
    archived: "bg-muted text-muted-foreground",
  };
  return <Badge className={`rounded-md font-medium uppercase tracking-wider text-[10px] ${map[status] || ""}`}>{status}</Badge>;
}

function formatDate(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
  } catch { return iso; }
}
